import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Loader2, Package } from "lucide-react";

const ORDER_STATUSES = ["PAID", "PROCESSING", "PACKED", "SHIPPED", "OUT_FOR_DELIVERY", "DELIVERED", "COMPLETED", "CANCELLED"];

function ManageOrder({ order, onDone }) {
  const [status, setStatus] = useState(order.status);
  const [tracking, setTracking] = useState(order.tracking_number || "");
  const [open, setOpen] = useState(false);
  const save = async () => {
    await api.put(`/admin/orders/${order.id}/status`, { status, tracking_number: tracking || null });
    toast.success("Order updated"); setOpen(false); onDone();
  };
  const refund = async () => {
    if (!window.confirm("Refund this order? This suspends digital access.")) return;
    await api.post(`/admin/orders/${order.id}/refund`); toast.success("Refund processed"); setOpen(false); onDone();
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline" className="border-white/20 text-slate-200" data-testid={`manage-${order.order_no}`}>Manage</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>{order.order_no}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="text-slate-500">{order.customer_name} · {order.email}</div>
          {order.shipping_address && <div className="bg-slate-100 rounded p-2 text-xs">{order.shipping_address.line1}, {order.shipping_address.city} {order.shipping_address.pincode}</div>}
          <div><label className="text-xs text-slate-500">Status</label>
            <select className="w-full border rounded-lg px-3 py-2" value={status} onChange={(e) => setStatus(e.target.value)}>{ORDER_STATUSES.map((s) => <option key={s}>{s}</option>)}</select></div>
          {order.requires_shipping && <div><label className="text-xs text-slate-500">Tracking number</label><Input value={tracking} onChange={(e) => setTracking(e.target.value)} /></div>}
          <div className="flex gap-2 pt-2">
            <Button className="bg-teal hover:bg-teal-dark text-white flex-1" onClick={save}>Update</Button>
            {order.payment_status === "PAID" && <Button variant="outline" className="text-red-600 border-red-200" onClick={refund}>Refund</Button>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminOrders() {
  const qc = useQueryClient();
  const { data: orders, isLoading } = useQuery({ queryKey: ["admin-orders"], queryFn: async () => (await api.get("/admin/orders")).data });
  if (isLoading) return <Loader2 className="w-8 h-8 animate-spin text-teal" />;
  return (
    <div>
      <h1 className="text-2xl font-bold text-white mb-6">Orders</h1>
      <div className="bg-slate-900 border border-white/10 rounded-xl overflow-x-auto">
        <table className="w-full text-sm min-w-[700px]">
          <thead className="bg-slate-800/50 text-slate-400 text-xs uppercase">
            <tr><th className="text-left px-4 py-3">Order</th><th className="text-left px-4 py-3">Customer</th><th className="text-left px-4 py-3">Total</th><th className="text-left px-4 py-3">Payment</th><th className="text-left px-4 py-3">Status</th><th className="text-right px-4 py-3"></th></tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {orders.map((o) => (
              <tr key={o.id} className="hover:bg-white/5" data-testid={`admin-order-${o.order_no}`}>
                <td className="px-4 py-3"><div className="font-mono text-white text-xs">{o.order_no}</div><div className="text-xs text-slate-500">{new Date(o.created_at).toLocaleDateString("en-IN")}</div></td>
                <td className="px-4 py-3 text-slate-300">{o.customer_name}<div className="text-xs text-slate-500">{o.email}</div></td>
                <td className="px-4 py-3 text-white">{inr(o.total)}</td>
                <td className="px-4 py-3"><span className={o.payment_status === "PAID" ? "text-teal-400" : o.payment_status === "REFUNDED" ? "text-red-400" : "text-amber-400"}>{o.payment_status}</span></td>
                <td className="px-4 py-3 text-slate-300">{o.status?.replace(/_/g, " ")}{o.requires_shipping && <Package className="w-3 h-3 inline ml-1 text-blue-400" />}</td>
                <td className="px-4 py-3 text-right"><ManageOrder order={o} onDone={() => qc.invalidateQueries(["admin-orders"])} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
